import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Layers,
  Construction,
  Wrench,
  Settings,
  Gauge,
  AlertTriangle,
  Cog,
  HardDrive,
  Activity,
  Ruler,
  ExternalLink,
  Home,
  Building2,
  Hammer,
  CircleDot,
  ShieldCheck,
  HelpCircle,
  Mountain,
  Factory
} from 'lucide-react';

const MicropieuxGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Qu'est-ce qu'un micropieu ?" },
    { id: 'section-2', title: "Les 4 types de micropieux" },
    { id: 'section-3', title: "Les étapes de mise en œuvre" },
    { id: 'section-4', title: "La reprise en sous-œuvre" },
    { id: 'section-5', title: "Les autres usages" },
    { id: 'section-6', title: "Micropieu ou pieu classique ?" },
    { id: 'section-7', title: "Questions fréquentes" },
    { id: 'section-8', title: "Matériel et contrôle" },
  ];

  const faqs = [
    {
      question: "Quelle est la différence entre un pieu et un micropieu ?",
      answer: "Le diamètre (moins de 300 mm pour le micropieu) et le mode de fonctionnement : le micropieu travaille essentiellement au frottement latéral, alors qu'un gros pieu s'appuie aussi en pointe. Le micropieu se distingue surtout par sa machine compacte, qui accède aux sites impossibles pour une foreuse classique."
    },
    {
      question: "Jusqu'à quelle profondeur descend un micropieu ?",
      answer: "Cela dépend entièrement de la profondeur de la couche porteuse. En reprise de maison individuelle, on trouve fréquemment le bon sol entre 5 et 15 mètres, mais cela varie selon la géologie locale — seule l'étude de sol le détermine."
    },
    {
      question: "Les micropieux sont-ils couverts par la garantie décennale ?",
      answer: "Oui. Participant à la stabilité de la construction, les travaux de micropieux et de reprise en sous-œuvre relèvent de l'assurance décennale de l'entreprise qui les réalise."
    },
    {
      question: "Peut-on faire des micropieux dans une cave ou un sous-sol ?",
      answer: "Oui, c'est justement leur intérêt principal. Les machines de forage compactes travaillent sous faible hauteur libre, ce qui rend possible la reprise en sous-œuvre depuis l'intérieur d'un bâtiment."
    },
    {
      question: "Faut-il une étude de sol avant des micropieux ?",
      answer: "Indispensable. Le type de micropieu, sa longueur et son dimensionnement dépendent entièrement du modèle de sol fourni par l'étude géotechnique (G2 pour un projet neuf, G5 en cas de désordres sur l'existant)."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Micropieux : types, mise en œuvre et reprise en sous-œuvre | Guide complet",
    "description": "Tout sur les micropieux : les 4 types (I à IV), mise en œuvre, reprise en sous-œuvre d'une maison, normes NF EN 14199 et DTU 13.2. Le guide complet.",
    "image": "https://geots.consulting/og-images/guide-micropieux.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-19",
    "dateModified": "2026-07-19"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Micropieu types data
  const micropieuxTypes = [
    { 
      type: "Type I", 
      method: "Foré tubé, rempli de mortier", 
      usage: "Technique ancienne, quasiment plus utilisée en France",
      color: "bg-gray-100 border-gray-300"
    },
    { 
      type: "Type II", 
      method: "Foré, scellé au coulis par gravité (tube plongeur)", 
      usage: "Charges modérées, reprise en sous-œuvre courante",
      color: "bg-blue-50 border-blue-300"
    },
    { 
      type: "Type III", 
      method: "Foré, scellé par injection globale sous pression (IGU)", 
      usage: "Portance renforcée",
      color: "bg-green-50 border-green-300"
    },
    { 
      type: "Type IV", 
      method: "Foré, scellé par injection répétitive et sélective (IRS)", 
      usage: "Fortes charges, terrains exigeants",
      color: "bg-purple-50 border-purple-300"
    },
  ];

  // Implementation steps
  const implementationSteps = [
    { 
      number: 1, 
      title: "Le forage", 
      desc: "Réalisé avec une machine compacte, à travers tous types de terrains (remblais, sols lâches, blocs, substratum rocheux). Diamètre inférieur à 300 mm.", 
      icon: Cog 
    },
    { 
      number: 2, 
      title: "Mise en place de l'armature", 
      desc: "Barre pleine ou tube d'acier, éventuellement équipé de manchettes pour l'injection (types III et IV).", 
      icon: Ruler 
    },
    { 
      number: 3, 
      title: "Scellement au coulis de ciment", 
      desc: "Par gravité via tube plongeur (type II), ou par injection sous pression (types III et IV).", 
      icon: Activity 
    },
    { 
      number: 4, 
      title: "Recépage et liaison", 
      desc: "Les micropieux sont raccordés à l'ouvrage, souvent par des longrines ou des massifs de reprise.", 
      icon: Layers 
    },
  ];

  // Other uses
  const otherUses = [
    { title: "Fondations d'ouvrages neufs", desc: "Sur sites contraints ou sols hétérogènes", icon: Building2 },
    { title: "Reprise en sous-œuvre", desc: "De bâtiments existants (cas le plus fréquent)", icon: Home },
    { title: "Ancrages en traction", desc: "Reprend aussi les efforts d'arrachement", icon: Target },
    { title: "Stabilisation de talus", desc: "Et de glissements de terrain", icon: Mountain },
    { title: "Fondations en site urbain dense", desc: "À proximité immédiate de mitoyens", icon: Factory },
    { title: "Ouvrages d'art", desc: "Et renforcement d'infrastructures existantes", icon: Construction },
  ];

  // Comparison data
  const comparisonData = [
    { criteria: "Diamètre", micropieu: "< 300 mm", pieu: "300 à 1200 mm et plus" },
    { criteria: "Fonctionnement", micropieu: "Frottement latéral", pieu: "Pointe + frottement" },
    { criteria: "Machine", micropieu: "Compacte, passe partout", pieu: "Grosse foreuse, accès nécessaire" },
    { criteria: "Charges", micropieu: "Modérées à fortes (selon type)", pieu: "Très fortes" },
    { criteria: "Terrain d'élection", micropieu: "Sites exigus, sous-œuvre, terrains difficiles", pieu: "Terrains dégagés, ouvrages lourds" },
  ];

  return (
    <>
      <Helmet>
        <title>Micropieux : types, mise en œuvre et reprise en sous-œuvre | Guide complet</title>
        <meta name="description" content="Tout sur les micropieux : les 4 types (I à IV), mise en œuvre, reprise en sous-œuvre d'une maison, normes NF EN 14199 et DTU 13.2. Le guide complet." />
        <link rel="canonical" href="https://geots.consulting/guides/micropieux" />
        <meta property="og:title" content="Micropieux : types, mise en œuvre et reprise en sous-œuvre | Guide complet" />
        <meta property="og:description" content="Tout sur les micropieux : les 4 types (I à IV), mise en œuvre, reprise en sous-œuvre d'une maison, normes NF EN 14199 et DTU 13.2. Le guide complet." />
        <meta property="og:url" content="https://geots.consulting/guides/micropieux" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
          <div className="container mx-auto px-4 py-12 md:py-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8 text-gray-300">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-white">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Micropieux</span>
            </nav>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-[#635BFF]/20 text-[#00D4FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <CircleDot className="w-4 h-4" />
                Guide technique
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Les micropieux : guide complet
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00D4FF] to-[#635BFF] mt-2">
                  (types, mise en œuvre et reprise en sous-œuvre)
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
                Un sous-sol trop exigu pour une foreuse classique ? Une maison qui bouge et qu'il faut stabiliser ? 
                Un terrain hétérogène en site urbain dense ? Le micropieu est souvent la réponse. 
                Petit par le diamètre, redoutablement polyvalent par ses usages. Ce guide fait le tour de la technique.
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Mis à jour : Juillet 2026</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Temps de lecture : 10 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl shadow-lg border p-6">
                  <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#635BFF]" />
                    Sommaire
                  </h3>
                  <nav className="space-y-1">
                    {sections.map((section, index) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="line-clamp-2">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Related Guides */}
                <div className="bg-gradient-to-br from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 mt-6 border border-[#635BFF]/10">
                  <h4 className="font-bold text-[#0A2540] mb-4">Guides connexes</h4>
                  <div className="space-y-3">
                    <Link to="/guides/fissures-maison-quand-s-inquieter" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Fissures maison : quand s'inquiéter ?
                    </Link>
                    <Link to="/guides/comment-choisir-fondations-speciales" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Choisir ses fondations spéciales
                    </Link>
                    <Link to="/guides/enregistreur-parametres-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Enregistreur de paramètres de forage
                    </Link>
                    <Link to="/guides/comment-choisir-etude-geotechnique" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Comment choisir son étude géotechnique
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Article */}
            <article className="flex-1 max-w-3xl">
              {/* Section 1: What is a micropieu? */}
              <section id="section-1" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <CircleDot className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  Qu'est-ce qu'un micropieu ?
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>
                    Le micropieu est un <strong>pieu foré de petit diamètre</strong>, scellé au coulis de ciment. 
                    La norme d'exécution européenne <strong>NF EN 14199</strong> le définit comme un pieu foré exécuté 
                    avec un outil de diamètre <strong>inférieur à 300 mm</strong> (les documents français, historiquement, 
                    retenaient 250 mm).
                  </p>

                  <p>
                    Sa particularité : il travaille essentiellement par <strong>frottement latéral</strong> avec le terrain, 
                    et non par appui en pointe comme un gros pieu. Le forage est équipé d'une armature — barre ou tube d'acier — 
                    puis rempli de coulis de ciment fortement dosé.
                  </p>

                  <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 my-6 border-l-4 border-[#635BFF]">
                    <p className="font-medium text-[#0A2540] mb-2 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-[#635BFF]" />
                      L'atout décisif : l'accessibilité
                    </p>
                    <p className="text-gray-700 mb-0">
                      La machine qui réalise un micropieu est compacte. Elle passe là où une foreuse de gros pieu 
                      resterait bloquée : dans une cave, un sous-sol, contre un mur mitoyen, sur une parcelle exiguë en centre-ville.
                    </p>
                  </div>

                  <p>
                    Autre avantage : le micropieu reprend aussi bien des efforts de <strong>compression que de traction</strong>, 
                    ce qui élargit considérablement son champ d'emploi.
                  </p>
                </div>
              </section>

              {/* Section 2: The 4 types */}
              <section id="section-2" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Layers className="w-5 h-5 text-blue-600" />
                  </span>
                  Les 4 types de micropieux
                </h2>

                <p className="text-gray-700 mb-6">
                  La classification française distingue <strong>quatre types</strong>, selon la façon dont le coulis est mis en place :
                </p>

                <div className="space-y-4 mb-8">
                  {micropieuxTypes.map((item, index) => (
                    <div 
                      key={index}
                      className={`rounded-xl p-5 border-2 ${item.color}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                          <span className="text-2xl font-bold text-[#0A2540]">{item.type.split(' ')[1]}</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-1">{item.type}</h3>
                          <p className="text-gray-700 text-sm mb-2">{item.method}</p>
                          <p className="text-gray-600 text-sm italic">{item.usage}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-amber-800 mb-2">Le principe à retenir</h4>
                      <p className="text-amber-700">
                        Plus on injecte sous pression (types III et IV), plus le coulis est plaqué contre le terrain, 
                        et plus le frottement latéral — donc la portance — augmente. Les <strong>types II</strong> sont plus simples 
                        et suffisent pour des charges modérées, ce qui en fait la solution la plus fréquente en reprise de maison individuelle.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 3: Implementation steps */}
              <section id="section-3" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-green-600" />
                  </span>
                  Les étapes de mise en œuvre
                </h2>

                <div className="space-y-6 mb-8">
                  {implementationSteps.map((step, index) => {
                    const IconComponent = step.icon;
                    return (
                      <div 
                        key={index}
                        className="flex items-start gap-4 bg-white rounded-xl p-6 border shadow-sm"
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {step.number}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <IconComponent className="w-5 h-5 text-[#635BFF]" />
                            <h3 className="font-bold text-[#0A2540]">{step.title}</h3>
                          </div>
                          <p className="text-gray-600">{step.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border">
                  <h4 className="font-bold text-[#0A2540] mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#635BFF]" />
                    Cadre normatif
                  </h4>
                  <p className="text-gray-700">
                    L'exécution relève de la norme <strong>NF EN 14199</strong>, complétée en France par le 
                    <strong> NF DTU 13.2</strong> (révisé en 2020) pour les fondations profondes. 
                    Le dimensionnement suit la norme <strong>NF P 94-262</strong>, application française de l'Eurocode 7.
                  </p>
                </div>
              </section>

              {/* Section 4: Underpinning */}
              <section id="section-4" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <Home className="w-5 h-5 text-red-600" />
                  </span>
                  L'application phare : la reprise en sous-œuvre
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>
                    C'est l'usage emblématique du micropieu. Quand une maison fissure parce que ses fondations bougent 
                    — souvent à cause du retrait-gonflement des argiles après une sécheresse — les micropieux permettent de 
                    <strong> reprendre les charges du bâtiment et de les reporter vers une couche de sol stable en profondeur</strong>.
                  </p>

                  <p>
                    Concrètement : des micropieux sont forés au droit des semelles existantes, vont chercher un horizon porteur 
                    (typiquement entre 5 et 15 mètres selon la géologie), et sont reliés par des longrines qui reprennent le bâtiment.
                  </p>
                </div>

                <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-red-800 mb-2">Point essentiel</h4>
                      <p className="text-red-700">
                        Une reprise en sous-œuvre ne s'improvise pas. Elle doit être précédée d'un <strong>diagnostic géotechnique</strong> (mission G5) 
                        qui identifie la cause réelle des désordres et dimensionne la solution. 
                        Reprendre un bâtiment sans avoir compris pourquoi il bouge, c'est traiter le symptôme et non la cause.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cross-link to fissures guide */}
                <div className="mt-8">
                  <Link 
                    to="/guides/fissures-maison-quand-s-inquieter"
                    className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                  >
                    <Home className="w-6 h-6 text-red-600" />
                    <div>
                      <p className="font-semibold text-red-800">Votre maison fissure ?</p>
                      <p className="text-sm text-red-600">Consultez notre guide sur les fissures de maison</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-red-600 ml-auto" />
                  </Link>
                </div>
              </section>

              {/* Section 5: Other uses */}
              <section id="section-5" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-purple-600" />
                  </span>
                  Les autres usages du micropieu
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  {otherUses.map((use, index) => {
                    const IconComponent = use.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-5 border hover:border-[#635BFF]/50 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-5 h-5 text-[#635BFF]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0A2540] mb-1">{use.title}</h3>
                            <p className="text-gray-600 text-sm">{use.desc}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 6: Comparison */}
              <section id="section-6" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-orange-600" />
                  </span>
                  Micropieu ou pieu classique : comment choisir ?
                </h2>

                <div className="bg-white rounded-xl border overflow-hidden shadow-sm mb-6">
                  <table className="w-full">
                    <thead className="bg-[#0A2540] text-white">
                      <tr>
                        <th className="px-4 py-4 text-left font-semibold">Critère</th>
                        <th className="px-4 py-4 text-left font-semibold">Micropieu</th>
                        <th className="px-4 py-4 text-left font-semibold">Pieu classique</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {comparisonData.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 font-medium text-[#0A2540]">{row.criteria}</td>
                          <td className="px-4 py-4 text-gray-600">{row.micropieu}</td>
                          <td className="px-4 py-4 text-gray-600">{row.pieu}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#00D4FF]">
                  <p className="text-gray-700 mb-0">
                    Le choix se fait toujours sur la base de l'<strong>étude géotechnique</strong> (mission G2), 
                    qui caractérise le sol et détermine la solution adaptée.
                  </p>
                </div>

                {/* Cross-link to fondations speciales */}
                <div className="mt-6">
                  <Link 
                    to="/guides/comment-choisir-fondations-speciales"
                    className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors"
                  >
                    <Construction className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-semibold text-blue-800">Aller plus loin</p>
                      <p className="text-sm text-blue-600">Guide complet : comment choisir ses fondations spéciales ?</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-blue-600 ml-auto" />
                  </Link>
                </div>
              </section>

              {/* Section 7: FAQ */}
              <section id="section-7" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-teal-600" />
                  </span>
                  Questions fréquentes
                </h2>

                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-[#0A2540] pr-4">{faq.question}</span>
                        {openFaq === index ? (
                          <ChevronUp className="w-5 h-5 text-[#635BFF] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === index && (
                        <div className="px-5 pb-5 text-gray-600 border-t">
                          <p className="pt-4">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 8: Equipment and control */}
              <section id="section-8" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  Réaliser des micropieux : matériel et contrôle
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>
                    Un chantier de micropieux mobilise une foreuse compacte, un atelier d'injection (centrale à coulis, pompe) 
                    et une instrumentation de contrôle. L'<strong>enregistrement des paramètres de forage et d'injection</strong> 
                    (pression, volume de coulis, profondeur) constitue la preuve de bonne exécution de chaque micropieu 
                    — un ouvrage enterré, donc invisible une fois terminé.
                  </p>
                </div>

                {/* Cross-link to enregistreur parametres */}
                <div className="mt-6">
                  <Link 
                    to="/guides/enregistreur-parametres-forage"
                    className="flex items-center gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition-colors"
                  >
                    <Gauge className="w-6 h-6 text-indigo-600" />
                    <div>
                      <p className="font-semibold text-indigo-800">Comprendre le contrôle qualité</p>
                      <p className="text-sm text-indigo-600">Guide : Enregistreur de paramètres de forage (DAC)</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-indigo-600 ml-auto" />
                  </Link>
                </div>

                {/* CTA Box */}
                <div className="mt-8 bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] rounded-2xl p-8 text-white">
                  <h3 className="text-xl font-bold mb-4">
                    Un projet de micropieux ?
                  </h3>
                  <p className="text-gray-300 mb-6">
                    GEOTS met en relation les acteurs de la géotechnique et des fondations spéciales : entreprises de micropieux 
                    et de reprise en sous-œuvre, bureaux d'études, loueurs de matériel de forage et d'instrumentation. 
                    Un projet à cadrer, du matériel à trouver ou un prestataire à identifier ?
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link 
                      to="/contact"
                      className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5248d9] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Contactez-nous
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link 
                      to="/annuaire/travaux-fondations-speciales/entreprises-micropieux"
                      className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Voir l'annuaire micropieux
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </section>

              {/* Disclaimer */}
              <div className="text-sm text-gray-500 italic border-t pt-8">
                Ce guide est fourni à titre informatif. La conception et la réalisation de micropieux doivent s'appuyer sur 
                une étude géotechnique et l'intervention d'un bureau d'études et d'une entreprise qualifiés.
              </div>
            </article>
          </div>
        </div>

        {/* Related Guides Section */}
        <div className="bg-gray-50 border-t py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-8 text-center">
              Continuez votre lecture
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link 
                to="/guides/fissures-maison-quand-s-inquieter"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <Home className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Fissures maison : quand s'inquiéter ?
                </h3>
                <p className="text-gray-600 text-sm">
                  Types de fissures, signaux d'alerte, causes et solutions.
                </p>
              </Link>

              <Link 
                to="/guides/comment-choisir-fondations-speciales"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <Construction className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Comment choisir ses fondations spéciales ?
                </h3>
                <p className="text-gray-600 text-sm">
                  Micropieux, pieux forés, injections... Quel procédé ?
                </p>
              </Link>

              <Link 
                to="/guides/enregistreur-parametres-forage"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                  <Gauge className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Enregistreur de paramètres de forage
                </h3>
                <p className="text-gray-600 text-sm">
                  DAC, contrôle qualité et traçabilité des fondations.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MicropieuxGuide;
